import React, { useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { X, Plus, Minus, Edit3 } from 'lucide-react';
import { InventoryItem } from '../../types/inventory';
import { inventoryAPI } from '../../services/inventoryAPI';

interface StockAdjustmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  item: InventoryItem;
}

const StockAdjustmentModal: React.FC<StockAdjustmentModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  item,
}) => {
  const { t } = useLanguage();
  
  const [adjustmentType, setAdjustmentType] = useState<'add' | 'remove' | 'set'>('add');
  const [quantity, setQuantity] = useState<number>(0);
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [errors, setErrors] = useState<any>({});
  const [loading, setLoading] = useState(false);

  const reasons = [
    { value: 'purchase', label: t('inventory.reasons.purchase') },
    { value: 'sale', label: t('inventory.reasons.sale') },
    { value: 'return', label: t('inventory.reasons.return') },
    { value: 'damage', label: t('inventory.reasons.damage') },
    { value: 'adjustment', label: t('inventory.reasons.adjustment') },
    { value: 'other', label: t('inventory.reasons.other') },
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    const newErrors: any = {};
    if (quantity <= 0) {
      newErrors.quantity = t('validation.positiveNumber');
    }
    if (!reason) {
      newErrors.reason = t('validation.required');
    }
    
    // Check if removing more than available
    if (adjustmentType === 'remove' && quantity > item.quantity_in_stock) {
      newErrors.quantity = t('inventory.errors.insufficientStock');
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    try {
      await inventoryAPI.adjustStock(item.id, {
        item_id: item.id,
        adjustment_type: adjustmentType,
        quantity,
        reason,
        notes,
      });

      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Error adjusting stock:', error);
      setErrors({ general: error.response?.data?.message || t('errors.generic') });
    } finally {
      setLoading(false);
    }
  };

  const getNewQuantity = () => {
    switch (adjustmentType) {
      case 'add':
        return item.quantity_in_stock + quantity;
      case 'remove':
        return Math.max(0, item.quantity_in_stock - quantity);
      case 'set':
        return quantity;
      default:
        return item.quantity_in_stock;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-bold text-gray-900">
            {t('inventory.adjustStock')}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-500"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Item Info */}
        <div className="bg-gray-50 rounded-lg p-4 mb-4">
          <h4 className="font-medium text-gray-900">{item.name}</h4>
          <p className="text-sm text-gray-600">SKU: {item.sku}</p>
          <p className="text-lg font-semibold text-gray-900 mt-2">
            {t('inventory.currentStock')}: {item.quantity_in_stock} {item.unit_of_measure}
          </p>
        </div>

        {errors.general && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-600 rounded-md">
            {errors.general}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Adjustment Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('inventory.adjustmentType')}
            </label>
            <div className="grid grid-cols-3 gap-2">
              <button
                type="button"
                onClick={() => setAdjustmentType('add')}
                className={`flex items-center justify-center px-3 py-2 border rounded-md transition-colors ${
                  adjustmentType === 'add'
                    ? 'border-green-500 bg-green-50 text-green-700'
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Plus className="w-4 h-4 mr-1" />
                {t('inventory.add')}
              </button>
              <button
                type="button"
                onClick={() => setAdjustmentType('remove')}
                className={`flex items-center justify-center px-3 py-2 border rounded-md transition-colors ${
                  adjustmentType === 'remove'
                    ? 'border-red-500 bg-red-50 text-red-700'
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Minus className="w-4 h-4 mr-1" />
                {t('inventory.remove')}
              </button>
              <button
                type="button"
                onClick={() => setAdjustmentType('set')}
                className={`flex items-center justify-center px-3 py-2 border rounded-md transition-colors ${
                  adjustmentType === 'set'
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Edit3 className="w-4 h-4 mr-1" />
                {t('inventory.set')}
              </button>
            </div>
          </div>

          {/* Quantity */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('inventory.quantity')} *
            </label>
            <input
              type="number"
              value={quantity}
              onChange={(e) => {
                setQuantity(parseInt(e.target.value) || 0);
                if (errors.quantity) {
                  setErrors({ ...errors, quantity: undefined });
                }
              }}
              min="0"
              className={`w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 ${
                errors.quantity ? 'border-red-300' : 'border-gray-300'
              }`}
            />
            {errors.quantity && (
              <p className="mt-1 text-sm text-red-600">{errors.quantity}</p>
            )}
            
            {/* Preview new quantity */}
            {quantity > 0 && (
              <p className="mt-2 text-sm text-gray-600">
                {t('inventory.newQuantity')}: {getNewQuantity()} {item.unit_of_measure}
              </p>
            )}
          </div>

          {/* Reason */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('inventory.reason')} *
            </label>
            <select
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
                if (errors.reason) {
                  setErrors({ ...errors, reason: undefined });
                }
              }}
              className={`w-full px-3 py-2 border rounded-md focus:ring-blue-500 focus:border-blue-500 ${
                errors.reason ? 'border-red-300' : 'border-gray-300'
              }`}
            >
              <option value="">{t('inventory.selectReason')}</option>
              {reasons.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
            {errors.reason && (
              <p className="mt-1 text-sm text-red-600">{errors.reason}</p>
            )}
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('inventory.notes')}
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              placeholder={t('inventory.notesPlaceholder')}
            />
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {loading ? t('common.saving') : t('inventory.confirmAdjustment')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default StockAdjustmentModal;